import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import {
  useDeletePlaceFaceMarkerMutation,
  useGetPlaceFaceMarkersQuery,
  useGetPlaceFaceQuery,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { AuthButton } from '../components/Button';
import { ConfirmDialog, GraphQLErrorDialog } from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { SectionHeader } from '../components/SectionHeader';
import { ToastContext } from '../components/Toast';
import { formatDate } from '../utils/DateHelper';
import './PlaceDxfMarkersPage.css';
import { TableScroll } from '../components/TableScroll';

type PlaceDxfMarkersPage = RouteComponentProps<{
  placeId: string;
  placeFaceId: string;
}>;

interface Marker {
  object_id: number;
  id: number;
  user_id: number;
  code_id: number;
  sort_id: number;
  work_id: number;
  step_id: number;
}

/** 表の列数。0件の行を表の幅いっぱいに広げるために使う */
const COLUMN_COUNT = 9;

export const PlaceDxfMarkersPage: React.FC<PlaceDxfMarkersPage> = function (
  props
) {
  const placeId = parseInt(props.match.params.placeId);
  const placeFaceId = parseInt(props.match.params.placeFaceId);
  const toastContext = React.useContext(ToastContext);

  const [deleteTarget, setDeleteTarget] = React.useState<Marker>();
  const getMarkersResult = useGetPlaceFaceMarkersQuery({
    variables: { placeId, placeFaceId },
  });
  // 見出しに出す図面名を取得する。
  // マーカーのクエリは同じ図面をIDなしで取得しており、キャッシュに書くと互いの内容を上書きし合うため、キャッシュを使わない
  const getFaceResult = useGetPlaceFaceQuery({
    variables: { placeFaceId },
    fetchPolicy: 'no-cache',
  });
  const [deleteMarker, deleteMarkerResult] = useDeletePlaceFaceMarkerMutation({
    onCompleted() {
      toastContext.showToast('削除しました', 'success');
      // 再取得の失敗はgetMarkersResult.errorに入り、画面のエラー表示が担うため、ここでは失敗の通知を受け流す
      getMarkersResult.refetch().catch(() => undefined);
    },
    onError() {
      toastContext.showToast('削除に失敗しました', 'error');
    },
  });

  if (getMarkersResult.error) {
    return <GraphQLErrorDialog error={getMarkersResult.error} />;
  }

  if (getFaceResult.error) {
    return <GraphQLErrorDialog error={getFaceResult.error} />;
  }

  if (!getMarkersResult.data || !getFaceResult.data) {
    return <Loading />;
  }

  const faceName = getFaceResult.data.getPlaceFace.dxf_file_name;
  const markers = getMarkersResult.data.getPlaceFace.markers;
  const accounts = getMarkersResult.data.getAccounts;
  const codes = getMarkersResult.data.getPlace.codes;
  const sorts = getMarkersResult.data.getPlace.sorts;
  const works = getMarkersResult.data.getPlace.works;
  const steps = works.flatMap((w) => [w.default_step, ...w.steps]);

  function getData(marker: Marker) {
    const account = accounts.find((a) => a.id === marker.user_id);
    const code = codes.find((c) => c.id === marker.code_id);
    const sort = sorts.find((s) => s.id === marker.sort_id);
    const work = works.find((w) => w.work_id === marker.work_id) ?? null;
    const step = steps.find((s) => s.step_id === marker.step_id) ?? null;

    return [account, code, sort, work, step];
  }

  let rows;
  if (markers.length === 0) {
    rows = <EmptyRow colSpan={COLUMN_COUNT} message="マーカーはありません" />;
  } else {
    rows = markers.map((marker) => {
      const [account, code, sort, work, step] = getData(marker);

      return (
        <tr key={marker.object_id}>
          <td>{marker.id}</td>
          <td>{sort?.name}</td>
          <td>{code?.name}</td>
          <td>{account?.name}</td>
          <td>{work?.name}</td>
          <td>{step?.name}</td>
          <td>{marker.revision}</td>
          <td>{formatDate(marker.created)}</td>
          <td className="App-table_button_cell">
            <div className="App-table_actions">
              <AuthButton
                authLv={2}
                color="red"
                mini={true}
                onClick={() => setDeleteTarget(marker)}
              >
                削除
              </AuthButton>
            </div>
          </td>
        </tr>
      );
    });
  }

  let deleteDialog;
  if (deleteTarget) {
    const [account, code, sort] = getData(deleteTarget);
    deleteDialog = (
      <ConfirmDialog
        open={true}
        danger={true}
        title="マーカーの削除"
        message={
          <div>
            <p>次のマーカーを削除しますか？</p>
            <ul className="PlaceDxfMarkersPage-confirm_list">
              <li>ID: {deleteTarget.id}</li>
              <li>作成者: {account?.name ?? 'なし'}</li>
              <li>補修コード: {code?.name ?? 'なし'}</li>
              <li>補修分類: {sort?.name ?? 'なし'}</li>
            </ul>
          </div>
        }
        yesLabel="削除"
        onYes={() => {
          deleteMarker({
            variables: { markerId: deleteTarget.object_id },
          });
          setDeleteTarget(undefined);
        }}
        onNo={() => setDeleteTarget(undefined)}
      />
    );
  }

  return (
    <div className="PlaceDxfMarkersPage">
      <Loading open={deleteMarkerResult.loading} />
      {deleteDialog}
      <Link className="PlaceDxfMarkersPage-back_link" to={`/places/${placeId}/faces`}>
        ← 図面一覧
      </Link>
      <SectionHeader title={`${faceName}のマーカー`} />
      <p className="App-table_count">{markers.length} 件</p>
      <TableScroll storageKey="placeFaceMarkers">
        <table className="App-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>補修分類</th>
              <th>補修コード</th>
              <th>作成者</th>
              <th>工種</th>
              <th>工程</th>
              <th>履歴番号</th>
              <th>作成日</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
